import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArchiveBoxIcon,
  CodeBracketIcon,
  CubeTransparentIcon,
  DocumentIcon,
  DocumentTextIcon,
  FolderIcon,
  MusicalNoteIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { DriveEntry } from '@/lib/drive/driveClient';
import { entryIconKind, formatBytes, formatTimestamp } from '@/lib/drive/format';
import { cachedThumbnail, isThumbnailable, loadThumbnail } from '@/lib/drive/thumbnails';

interface DriveFileListProps {
  entries: DriveEntry[];
  viewMode: 'grid' | 'list';
  selectedPaths: Set<string>;
  locale?: string;
  /** Plain click — single selection. */
  onSelect: (entry: DriveEntry) => void;
  /** Click with keyboard modifiers — ctrl toggles, shift ranges. */
  onSelectModifiers: (entry: DriveEntry, event: React.MouseEvent) => void;
  onOpen: (entry: DriveEntry) => void;
  onContextMenu: (entry: DriveEntry, x: number, y: number) => void;
  /** Drag-and-drop move: the dragged entry is dropped on a folder entry. */
  onDropOnFolder: (folder: DriveEntry, dragged: DriveEntry) => void;
}

const ICONS: Record<string, typeof DocumentIcon> = {
  folder: FolderIcon,
  image: PhotoIcon,
  video: VideoCameraIcon,
  audio: MusicalNoteIcon,
  pdf: DocumentTextIcon,
  text: DocumentTextIcon,
  code: CodeBracketIcon,
  archive: ArchiveBoxIcon,
  file: DocumentIcon,
  model: CubeTransparentIcon,
};

/** One grid tile's visual: a cached image thumbnail when one exists, the type icon otherwise. */
function GridThumb({ entry, Icon }: { entry: DriveEntry; Icon: typeof DocumentIcon }) {
  const [url, setUrl] = useState<string | null>(() => cachedThumbnail(entry.id));
  useEffect(() => {
    let alive = true;
    if (!isThumbnailable(entry)) return;
    void loadThumbnail(entry).then((next) => {
      if (alive && next) setUrl(next);
    });
    return () => {
      alive = false;
    };
  }, [entry]);
  if (url) {
    return <img src={url} alt="" className="h-16 w-16 flex-shrink-0 rounded-md object-cover" loading="lazy" />;
  }
  return <Icon className="h-8 w-8 flex-shrink-0 text-accent" />;
}

/** The main listing: responsive card grid or a compact detailed list. */
export function DriveFileList({
  entries,
  viewMode,
  selectedPaths,
  locale,
  onSelect,
  onSelectModifiers,
  onOpen,
  onContextMenu,
  onDropOnFolder,
}: DriveFileListProps) {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return (
      <div className="flex min-h-48 flex-1 items-center justify-center rounded-lg border border-border-color bg-card">
        <p className="text-sm text-muted-foreground">{t('drive.empty')}</p>
      </div>
    );
  }

  const iconOf = (entry: DriveEntry) =>
    ICONS[entryIconKind(entry.name, entry.mimeType, entry.kind === 'folder')] ?? DocumentIcon;

  const cardHandlers = (entry: DriveEntry) => ({
    draggable: true,
    onClick: (event: React.MouseEvent) => {
      if (event.ctrlKey || event.metaKey || event.shiftKey) onSelectModifiers(entry, event);
      else onSelect(entry);
    },
    onDoubleClick: () => onOpen(entry),
    onContextMenu: (event: React.MouseEvent) => {
      event.preventDefault();
      onContextMenu(entry, event.clientX, event.clientY);
    },
    onDragStart: (event: React.DragEvent) => {
      event.dataTransfer.setData('application/x-sak-entry', JSON.stringify(entry));
      event.dataTransfer.effectAllowed = 'move';
    },
    ...(entry.kind === 'folder'
      ? {
          onDragOver: (event: React.DragEvent) => {
            if (event.dataTransfer.types.includes('application/x-sak-entry')) {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
            }
          },
          onDrop: (event: React.DragEvent) => {
            event.preventDefault();
            event.stopPropagation();
            const raw = event.dataTransfer.getData('application/x-sak-entry');
            if (!raw) return;
            try {
              const dragged = JSON.parse(raw) as DriveEntry;
              if (dragged.path !== entry.path) onDropOnFolder(entry, dragged);
            } catch {
              /* not ours — ignore */
            }
          },
        }
      : {}),
  });

  if (viewMode === 'grid') {
    return (
      <div
        className="grid min-h-48 flex-1 grid-cols-[repeat(auto-fill,minmax(160px,1fr))] content-start gap-3 rounded-lg border border-border-color bg-card p-3"
        data-tour-id="file-list"
      >
        {entries.map((entry) => {
          const Icon = iconOf(entry);
          const isSelected = selectedPaths.has(entry.path);
          return (
            <button
              key={entry.id}
              type="button"
              {...cardHandlers(entry)}
              className={clsx(
                'flex min-w-0 flex-col items-center gap-2 rounded-lg border p-3 text-center transition-colors',
                isSelected
                  ? 'border-accent bg-active'
                  : 'border-transparent hover:bg-muted',
              )}
            >
              <GridThumb entry={entry} Icon={Icon} />
              <span className="w-full truncate text-sm font-medium text-foreground" title={entry.name}>
                {entry.name}
              </span>
              <span className="w-full truncate text-xs text-muted-foreground">
                {entry.kind === 'folder'
                  ? t('drive.kindFolder')
                  : formatBytes(entry.size)}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-x-auto rounded-lg border border-border-color bg-card"
      data-tour-id="file-list"
    >
      <table className="w-full min-w-[480px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border-color text-start text-xs uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="px-3 py-2 text-start font-medium">{t('drive.name')}</th>
            <th scope="col" className="w-24 px-3 py-2 text-start font-medium">{t('drive.size')}</th>
            <th scope="col" className="w-48 px-3 py-2 text-start font-medium">{t('drive.modified')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const Icon = iconOf(entry);
            const isSelected = selectedPaths.has(entry.path);
            return (
              <tr
                key={entry.id}
                {...cardHandlers(entry)}
                className={clsx(
                  'cursor-default border-b border-border-color/50 last:border-b-0',
                  isSelected ? 'bg-active' : 'hover:bg-muted',
                )}
              >
                <td className="min-w-0 px-3 py-2">
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4 flex-shrink-0 text-accent" />
                    <span className="truncate whitespace-nowrap text-foreground" title={entry.name}>
                      {entry.name}
                    </span>
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {entry.kind === 'folder' ? '—' : formatBytes(entry.size)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {formatTimestamp(entry.updatedAt || entry.createdAt, locale)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
